import { useCallback, useEffect, useState } from 'react';

import type { Document, ShareData, ShareResponse } from '../common/types';
import { AuthService } from '../services/AuthService';
import { DocuIdApiService } from '../services/DocuIdApiService';
import { DocumentService } from '../services/document';
import { createLogger } from '../services/Logger';

const log = createLogger('useDocuments');

export function useDocuments() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isOpeningDocument, setIsOpeningDocument] = useState<string | null>(null);
  const [isClosingDocument, setIsClosingDocument] = useState<string | null>(null);
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);

  useEffect(() => {
    if (AuthService.getStoredAuth()) {
      loadDocuments();
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      setIsLoadingDocuments(true);
      const docs = await DocumentService.getDocuments();
      setDocuments(docs);
    } catch (error) {
      log.error(
        'Failed to load documents',
        error instanceof Error ? error : new Error(String(error)),
      );
    } finally {
      setIsLoadingDocuments(false);
    }
  }, []);

  const openDocument = useCallback(async (document: Document) => {
    try {
      setIsOpeningDocument(document.id);
      await DocumentService.openDocument(document.id);
      setOpenDocumentId(document.id);
    } catch (error) {
      log.error(
        'Failed to open document',
        error instanceof Error ? error : new Error(String(error)),
      );
    } finally {
      setIsOpeningDocument(null);
    }
  }, []);

  const closeDocument = useCallback(async (documentId: string) => {
    try {
      setIsClosingDocument(documentId);
      await DocumentService.closeDocument(documentId);
      setOpenDocumentId((previous) => (previous === documentId ? null : previous));
    } catch (error) {
      log.error(
        'Failed to close document',
        error instanceof Error ? error : new Error(String(error)),
      );
    } finally {
      setIsClosingDocument(null);
    }
  }, []);

  const shareDocument = useCallback(async (shareData: ShareData): Promise<ShareResponse> => {
    const response = await DocuIdApiService.shareDocument({
      documentId: shareData.documentId,
      email: shareData.email,
      countryCode: shareData.countryCode,
      mobile: shareData.mobile,
      message: shareData.message,
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to share document');
    }

    return {
      shareLink: response.data?.shareLink,
      shareId: response.data?.shareId,
      message: response.message,
    };
  }, []);

  const saveEdits = useCallback(
    async (documentId: string) => {
      setIsSavingEdits(true);
      try {
        await DocumentService.saveEdits(documentId);
        await loadDocuments();
      } finally {
        setIsSavingEdits(false);
      }
    },
    [loadDocuments],
  );

  const saveAsNew = useCallback(
    async (fileName: string, workspaceId?: string, folderId?: string) => {
      await DocumentService.saveAsNew(fileName, workspaceId, folderId);
      await loadDocuments();
    },
    [loadDocuments],
  );

  const clearDocuments = useCallback(async () => {
    await DocumentService.clearDocument();
    setDocuments([]);
    setOpenDocumentId(null);
  }, []);

  return {
    documents,
    isLoadingDocuments,
    isOpeningDocument,
    isClosingDocument,
    isSavingEdits,
    openDocumentId,
    loadDocuments,
    openDocument,
    closeDocument,
    shareDocument,
    saveEdits,
    saveAsNew,
    clearDocuments,
  };
}
